import {announce} from '@react-aria/live-announcer'
import Big from 'big.js'

import getAccountInformation from '@/auth/utils/getAccountInformation'
import useDiscountAmount, {
  getDiscountQueryKey,
  getDiscountQueryOptions,
} from '@/hooks/useDiscountAmount'
import {useCart} from '@/lib/cart/hooks/useCart'
import {useCurrencySymbol} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import useCashback from '@/lib/payment/hooks/useCashback'
import useCoupon from '@/lib/payment/hooks/useCoupon'
import useTax, {getTaxQueryKey, getTaxQueryOptions} from '@/lib/payment/hooks/useTax'
import {type MinimalCartItem, type StripeAddress, Tax} from '@/lib/payment/paymenModel'
import {useCustomCartValue, useRoundUp} from '@/lib/payment/store'
import {Discount} from '@/services/rewardService'

export default function useCartSummary(
  isRewardsApplied: boolean,
  coupon: string,
  setCoupon?: (coupon: string) => void | Promise<void>,
  address?: StripeAddress,
  custom?: boolean,
) {
  const [locale] = useLocale()
  const currencySymbol = useCurrencySymbol()
  const latestCurrencySymbol = useLatest(currencySymbol)

  const {data: defaultCart} = useCart(locale, !custom)
  const customCart = useCustomCartValue()
  const cart = custom ? customCart : defaultCart

  const [roundUp] = useRoundUp()

  const couponRef = useRef<string>('')
  const [couponIsDirty, setCouponIsDirty] = useState(false)
  const applyCoupon = useCallback(() => {
    void setCoupon?.(couponRef.current)
  }, [setCoupon])

  const {
    data: couponInfo,
    error: couponError,
    isFetching: isCouponLoading,
  } = useCoupon(coupon, !isRewardsApplied)

  useEffect(
    function announceCouponSuccess() {
      if (couponInfo) {
        announce(`Coupon applied: -${latestCurrencySymbol.current}${couponInfo.amount}`)
      }
    },
    [couponInfo, couponInfo?.code],
  )

  useEffect(
    function announceCouponError() {
      if (couponError === null) return
      announce(couponError.message)
    },
    [couponError, couponError?.message],
  )

  const handleCouponChange = useCallback(
    (value: string) => {
      couponRef.current = value
      if (value === '') {
        setCouponIsDirty(false)
      } else {
        setCouponIsDirty(true)
      }
      void setCoupon?.('')
    },
    [setCoupon],
  )

  const cartItemNumber = cart?.itemsLength ?? 0
  const subtotal = cart?.itemsValue ?? Big(0)

  const deliveryFee = 0
  const totalBeforeTax = Big(subtotal).plus(deliveryFee)

  const lineItems = Object.values(cart?.items ?? {}).reduce<MinimalCartItem[]>((acc, item) => {
    item.forEach(item => {
      acc.push({
        id: item.retailer + item.id,
        quantity: item.quantity,
        price: item.price,
      })
    })
    return acc
  }, [])

  const {data: tax, isLoading: isTaxLoading} = useTax({
    address,
    lineItems,
  })

  const totalAfterTax = totalBeforeTax.plus(Big(tax?.taxAmount ?? 0))

  const roundUpAmount = totalAfterTax.round(0, Big.roundUp).minus(totalAfterTax)

  const totalAfterRoundUp = roundUp ? totalAfterTax.plus(roundUpAmount) : totalAfterTax

  const {data: {discount: discountValue} = {discount: 0}} = useDiscountAmount(totalAfterRoundUp)
  const derivedDiscountValue = (() => {
    if (isRewardsApplied) {
      return discountValue
    }

    if (couponInfo) {
      return couponInfo.amount
    }

    return 0
  })()

  let total = totalAfterRoundUp.minus(derivedDiscountValue)
  if (total.lt(0)) {
    total = Big(0)
  }

  const {data: cashback} = useCashback(total)

  const isLoading = isTaxLoading || isCouponLoading

  return {
    cashback,
    cartItemNumber,
    total,
    totalBeforeTax,
    isTaxLoading,
    tax: tax?.taxAmount ?? 0,
    discountValue,
    derivedDiscountValue,
    subtotal,
    cart,
    custom,
    applyCoupon,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    isLoading,
    coupon,
    couponIsDirty,
    totalAfterTax,
    roundUpAmount,
    totalAfterRoundUp,
  }
}

export async function getTaxAndDiscount({
  queryClient,
  roundUp,
  taxQueryParams,
}: {
  queryClient: QueryClient
  roundUp: boolean
  taxQueryParams: Parameters<typeof getTaxQueryKey>[0]
}) {
  const taxQueryKey = getTaxQueryKey(taxQueryParams)

  const taxData =
    queryClient.getQueryData(taxQueryKey) ??
    (await queryClient.fetchQuery(getTaxQueryOptions(taxQueryParams)))
  const taxAmount = Big(Tax(taxData).taxAmount)

  const subtotal =
    taxQueryParams.lineItems?.reduce(
      (acc, item) => acc.plus(Big(item.price).times(item.quantity)),
      Big(0),
    ) ?? Big(0)

  const deliveryFee = 0

  const totalAfterTax = subtotal.plus(deliveryFee).plus(taxAmount)

  const roundUpAmount = totalAfterTax.round(0, Big.roundUp).minus(totalAfterTax)

  const totalAfterRoundUp = roundUp ? totalAfterTax.plus(roundUpAmount) : totalAfterTax

  const discountQueryParams = {
    price: totalAfterRoundUp,
    account: getAccountInformation(),
  }

  const discountQueryKey = getDiscountQueryKey(discountQueryParams)

  const discountData =
    queryClient.getQueryData(discountQueryKey) ??
    (await queryClient.fetchQuery(getDiscountQueryOptions(discountQueryParams)))

  const discountValue = Big(Discount(discountData).discount)

  return {
    tax: taxAmount,
    discount: discountValue,
    roundUp,
    roundUpAmount,
  }
}
